import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { getDomainColor, getDomainColorWithOpacity, getContrastingTextColor } from '../../services/domainColorService.js';

export interface TailoredActivityData {
  id: string;
  name: string;
  description: string;
  instructions?: string; // Instructions from LLM tailoring process
  domain: string;
  duration_minutes: number;
  base_challenge_rating: number;
  percentage: number;
  listColor?: string;
  extremityColor?: string;
  color?: string;
  adaptations?: string[];
  confidence?: number;
  version?: number;
  created_at?: string;
  updated_at?: string;
  activity_tailored_id?: string; // For fetching full details
  wheel_item_id?: string; // For compatibility
}

@customElement('tailored-activity-details-modal')
export class TailoredActivityDetailsModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Object }) activity: TailoredActivityData | null = null;
  @property({ type: Boolean }) loading = false;
  @property({ type: Object }) fullActivityData: TailoredActivityData | null = null;

  static styles = css`
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(12px);
      z-index: 2100; /* Tier 2: Activity details can overlay during wheel interaction */
      display: flex;
      align-items: flex-start;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
      overflow-y: auto;
      padding: var(--spacing-4) var(--spacing-2);
    }

    .modal-overlay.open {
      opacity: 1;
      pointer-events: all;
    }

    .modal-content {
      background: #ffffff;
      border-radius: 20px;
      width: 90%;
      max-width: 650px;
      min-height: 300px;
      max-height: calc(100vh - 80px);
      overflow: hidden;
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15), 0 10px 20px rgba(0, 0, 0, 0.1);
      border: 1px solid rgba(0, 0, 0, 0.08);
      display: flex;
      flex-direction: column;
      transform: scale(0.9) translateY(20px);
      transition: all 0.3s ease;
      margin: auto 0;
    }

    .modal-overlay.open .modal-content {
      transform: scale(1) translateY(0);
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--spacing-4) var(--spacing-5);
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    }

    .modal-title {
      font-size: var(--font-size-xl);
      font-weight: 700;
      color: #1e293b;
      margin: 0;
      flex: 1;
      min-width: 0;
      letter-spacing: -0.025em;
    }

    .modal-close {
      background: none;
      border: none;
      font-size: var(--font-size-xl);
      color: #64748b;
      cursor: pointer;
      padding: var(--spacing-2);
      border-radius: 8px;
      transition: all 0.2s ease;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-close:hover {
      background: rgba(0, 0, 0, 0.05);
      color: #1e293b;
      transform: scale(1.05);
    }

    .modal-body {
      padding: var(--spacing-5);
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      min-height: 0;
      max-height: calc(100vh - 160px);
      background: #ffffff;
      scrollbar-width: thin;
      scrollbar-color: #cbd5e1 #f1f5f9;
    }

    .modal-body::-webkit-scrollbar {
      width: 6px;
    }

    .modal-body::-webkit-scrollbar-track {
      background: #f1f5f9;
      border-radius: 3px;
    }

    .modal-body::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;
    }

    .modal-body::-webkit-scrollbar-thumb:hover {
      background: #94a3b8;
    }

    .activity-header {
      display: flex;
      align-items: center;
      gap: var(--spacing-4);
      margin-bottom: var(--spacing-5);
      padding: var(--spacing-4);
      border-radius: 16px;
      border-left: 5px solid;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }

    .activity-color-dot {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      flex-shrink: 0;
      border: 3px solid rgba(255, 255, 255, 0.8);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .activity-info {
      flex: 1;
      min-width: 0;
    }

    .activity-name {
      font-size: var(--font-size-xl);
      font-weight: 700;
      color: #1e293b;
      margin-bottom: var(--spacing-2);
      letter-spacing: -0.025em;
      line-height: 1.3;
    }

    .activity-badges {
      display: flex;
      gap: var(--spacing-2);
      flex-wrap: wrap;
    }

    .domain-badge {
      display: inline-block;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: var(--font-size-xs);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .section {
      margin-bottom: var(--spacing-4);
    }

    .section-title {
      font-size: var(--font-size-lg);
      font-weight: 700;
      color: #1e293b;
      margin-bottom: var(--spacing-3);
      display: flex;
      align-items: center;
      gap: var(--spacing-2);
      letter-spacing: -0.025em;
    }

    .section-content {
      background: #f8fafc;
      padding: var(--spacing-4);
      border-radius: 12px;
      border: 1px solid #e2e8f0;
      color: #475569;
      line-height: 1.7;
      white-space: pre-wrap;
      word-wrap: break-word;
      max-height: none;
      font-size: var(--font-size-base);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    }

    .meta-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: var(--spacing-3);
      margin-bottom: var(--spacing-4);
    }

    .meta-item {
      background: #f8fafc;
      padding: var(--spacing-4);
      border-radius: 12px;
      border: 1px solid #e2e8f0;
      text-align: center;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      transition: all 0.2s ease;
    }

    .meta-item:hover {
      background: #f1f5f9;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    }

    .meta-label {
      font-size: var(--font-size-xs);
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.75px;
      margin-bottom: var(--spacing-2);
      font-weight: 600;
    }

    .meta-value {
      font-size: var(--font-size-lg);
      font-weight: 700;
      color: #1e293b;
      letter-spacing: -0.025em;
    }

    .adaptations-list {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .adaptation-item {
      background: #f8fafc;
      padding: var(--spacing-3) var(--spacing-4);
      border-radius: 10px;
      border-left: 4px solid var(--color-primary);
      margin-bottom: var(--spacing-3);
      color: #475569;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      transition: all 0.2s ease;
      line-height: 1.6;
    }

    .adaptation-item:hover {
      background: #f1f5f9;
      transform: translateX(2px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    }

    .adaptation-item:last-child {
      margin-bottom: 0;
    }

    .no-data {
      color: #94a3b8;
      font-style: italic;
      text-align: center;
      padding: var(--spacing-4);
      background: #f8fafc;
      border-radius: 8px;
      border: 1px dashed #cbd5e1;
    }

    .loading-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-5);
      color: #64748b;
    }

    .loading-spinner {
      width: 32px;
      height: 32px;
      border: 3px solid #e2e8f0;
      border-top: 3px solid #3b82f6;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: var(--spacing-3);
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    /* Special styling for instructions section */
    .instructions-section .section-content {
      background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
      border: 1px solid #f59e0b;
      border-left: 4px solid #f59e0b;
      font-weight: 500;
    }

    /* Enhanced section spacing */
    .section + .section {
      margin-top: var(--spacing-5);
    }

    /* Improved emoji styling in section titles */
    .section-title span {
      font-size: 1.2em;
      margin-right: var(--spacing-1);
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
    }
  `;

  private closeModal = () => {
    this.open = false;
    this.fullActivityData = null;
    this.loading = false;
    this.dispatchEvent(new CustomEvent('close', { bubbles: true }));
  };

  private handleOverlayClick = (e: Event) => {
    if (e.target === e.currentTarget) {
      this.closeModal();
    }
  };

  private async fetchFullActivityDetails() {
    // For now, just use the activity data as-is since the API endpoint may not be available
    // This ensures the modal works with the current wheel item data structure
    this.loading = true;
    this.requestUpdate();

    try {
      // Simulate a brief loading period for UX
      await new Promise(resolve => setTimeout(resolve, 500));

      // Use the activity data as-is for now
      // In the future, this would fetch full details from the API
      this.fullActivityData = this.activity ? {
        ...this.activity,
        // Ensure required fields are present
        id: this.activity.id || '',
        name: this.activity.name || '',
        description: this.activity.description || '',
        instructions: this.activity.instructions || '', // CRITICAL FIX: Preserve instructions field
        domain: this.activity.domain || 'general',
        duration_minutes: this.activity.duration_minutes || 20,
        base_challenge_rating: this.activity.base_challenge_rating || 50,
        percentage: this.activity.percentage || 0,
      } : null;

      console.log('Activity data loaded:', this.fullActivityData);
    } catch (error) {
      console.error('Error processing activity details:', error);
      this.fullActivityData = this.activity;
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('open') && this.open && this.activity) {
      this.fetchFullActivityDetails();
    }
  }

  render() {
    if (!this.activity) return '';

    // Use full activity data if available, otherwise fall back to basic activity data
    const displayActivity = this.fullActivityData || this.activity;
    const listColor = displayActivity.listColor || displayActivity.extremityColor || displayActivity.color || getDomainColor(displayActivity.domain);
    const backgroundColorWithOpacity = getDomainColorWithOpacity(displayActivity.domain, 0.1);
    const textColor = getContrastingTextColor(displayActivity.domain);

    return html`
      <div class="modal-overlay ${this.open ? 'open' : ''}" data-testid="activity-details-overlay" @click=${this.handleOverlayClick}>
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title">Activity Details</h2>
            <button class="modal-close" data-testid="activity-details-modal-close-btn" @click=${this.closeModal}>×</button>
          </div>
          
          <div class="modal-body">
            ${this.loading ? html`
              <div class="loading-state">
                <div class="loading-spinner"></div>
                <p>Loading activity details...</p>
              </div>
            ` : html`
              <div class="activity-header" style="border-left-color: ${listColor}; background: ${backgroundColorWithOpacity};">
                <div class="activity-color-dot" style="background-color: ${listColor}; box-shadow: 0 0 8px ${getDomainColorWithOpacity(displayActivity.domain, 0.4)};"></div>
                <div class="activity-info">
                  <div class="activity-name" data-testid="activity-details-name">${displayActivity.name}</div>
                  <div class="activity-badges">
                    <span class="domain-badge" data-testid="activity-details-domain" style="background-color: ${listColor}; color: ${textColor};">${displayActivity.domain}</span>
                  </div>
                </div>
              </div>



              <div class="section">
                <div class="section-title">
                  <span>📝</span>
                  Description
                </div>
                <div class="section-content" data-testid="activity-details-description">
                  ${displayActivity.description || html`<div class="no-data">No description available</div>`}
                </div>
              </div>

              ${displayActivity.instructions ? html`
                <div class="section instructions-section">
                  <div class="section-title">
                    <span>📋</span>
                    Instructions
                  </div>
                  <div class="section-content">
                    ${displayActivity.instructions}
                  </div>
                </div>
              ` : html`
                <div class="section instructions-section">
                  <div class="section-title">
                    <span>📋</span>
                    Instructions
                  </div>
                  <div class="section-content">
                    <div class="no-data">
                      Instructions are not available for this activity. This may be because the activity is still being processed or the instructions were not generated during the tailoring process.
                    </div>
                  </div>
                </div>
              `}

              ${displayActivity.adaptations && displayActivity.adaptations.length > 0 ? html`
                <div class="section">
                  <div class="section-title">
                    <span>🎯</span>
                    Tailored Adaptations
                  </div>
                  <ul class="adaptations-list">
                    ${displayActivity.adaptations.map(adaptation => html`
                      <li class="adaptation-item">${adaptation}</li>
                    `)}
                  </ul>
                </div>
              ` : ''}
            `}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tailored-activity-details-modal': TailoredActivityDetailsModal;
  }
}
